/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import PropTypes from 'prop-types' // Npm: react.js library.
import _ from 'underscore' // Npm: underscore library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { toast } from 'react-hot-toast' // Npm: React toastify.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import Modal from 'components/Modal'
import QuickFileUpload from 'components/QuickFileUpload'
import TableSpinner from 'components/TableSpinner'
import SenderIdDropDownOptions from 'components/SenderIdsDropDownOptions'
import SenderIdUpsert from 'components/SenderIdsUpsert'
import SenderIdDelete from 'components/SenderIdsDelete'


/*
 * GRAPHS
 */
import SenderIdReadQuery from './__query__/index.senderId.read.query'
import SenderIdUploadMutation from './__mutation__/index.senderId.upload.mutation'


/*
 * STYLES
 */
import {
  buttonStyle
} from './index.style'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {

  const _successFlags = React.useMemo(() => Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key), [])

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [searchQuery, setSearchQuery] = React.useState('')
  const [MutationSenderIdUpload] = useMutation(SenderIdUploadMutation)
  const { 'isOpen': isSenderIdCreateOpen, 'onOpen': onSenderIdCreateOpen, 'onClose': onSenderIdCreateClose } = useDisclosure()
  const { 'isOpen': isSenderIdUpdateOpen, 'onOpen': onSenderIdUpdateOpen, 'onClose': onSenderIdUpdateClose } = useDisclosure()
  const { 'isOpen': isSenderIdDeleteOpen, 'onOpen': onSenderIdDeleteOpen, 'onClose': onSenderIdDeleteClose } = useDisclosure()
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)
  const _rowCountRef = React.useRef(0)
  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false
    setPaginationModel(j => {
      const nextPaginationModel = { ...j, 'page': 0 }

      _paginationModelRef.current = nextPaginationModel

      return nextPaginationModel
    })
  }, [])
  const _QuerySenderIdReadVariables = React.useMemo(() => ({
    senderIdDirectoryId: passOn?.senderIdDirectoryId,
    senderIdToSearch: searchQuery,
    skip: paginationModel?.pageSize * paginationModel?.page,
    take: paginationModel?.pageSize
  }), [paginationModel?.page, paginationModel?.pageSize, passOn?.senderIdDirectoryId, searchQuery])
  const _QuerySenderIdRead = useQuery(SenderIdReadQuery, {
    variables: _QuerySenderIdReadVariables,
    fetchPolicy: _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy,
  });
  const _onSearchInputChange = React.useRef(
    _.debounce((value) => {
      _ResetPagination()
      setSearchQuery(value)
    }, 800),
  ).current;
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])
  const _HandleFilterModelChange = React.useCallback(__filterModel => {
    _ResetPagination()
    setFilterModel(__filterModel)
  }, [_ResetPagination])
  const _HandleColumnVisibilityModelChange = React.useCallback(newModel => {
    setColumnVisibilityModel(newModel)
  }, [])
  const _QuerySenderIdReadRowCount = _.pluck(_QuerySenderIdRead?.data?.SenderIdRead, '_totalCount')?.[0]
  const rowCount = React.useMemo(() => {
    if (undefined !== _QuerySenderIdReadRowCount && null !== _QuerySenderIdReadRowCount) _rowCountRef.current = _QuerySenderIdReadRowCount

    return _rowCountRef.current
  }, [_QuerySenderIdReadRowCount])
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  }) ?? false;

  // Extract search from filter model
  React.useEffect(() => {
    const senderIdFilter = filterModel?.items?.find(
      (item) => item.field === "senderId",
    );
    const searchValue = senderIdFilter?.value || "";
    _onSearchInputChange(searchValue);
  }, [_onSearchInputChange, filterModel]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.();
    };
  }, []);

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _QuerySenderIdRead.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_QuerySenderIdRead.refetch]);

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 250
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'senderId',
      'headerName': 'SenderId',
      'description': 'SenderId name.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 330,
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Created date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 310,
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Updated date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'width': 310,
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for senderId.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 230,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <SenderIdDropDownOptions
        senderIdId={row?.id}
        senderIdDirectoryId={passOn?.senderIdDirectoryId}
        onSenderIdUpdate={onSenderIdUpdateOpen}
        onSenderIdDelete={onSenderIdDeleteOpen}
      />
    }
  ], [passOn?.senderIdDirectoryId, onSenderIdUpdateOpen, onSenderIdDeleteOpen])

  const rows = React.useMemo(() => _.compact(_QuerySenderIdRead?.data?.SenderIdRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    'id': r?.id ?? `senderids-directory-item-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'senderId': r?.senderId ?? '-',
    'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD'),
    'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD')
  }) : null)), [_QuerySenderIdRead?.data?.SenderIdRead, _successFlags, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className="senderIds base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="81vh"
          maxH="81vh"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex pb="0px" justify="space-between" gap="12px">
            <Tooltip label="Create New SenderId" fontSize="sm">
              <Button
                w="170px"
                leftIcon={<IoMdAdd />}
                onClick={onSenderIdCreateOpen}
                style={buttonStyle}
              >
                Create Sender Id
              </Button>
            </Tooltip>
            <Flex gap="12px">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <QuickFileUpload
                onChange={(j) => {
                  // Const assignment.
                  const _senderIds = Array.from(
                    { length: j?.rows?.length },
                    (__, i) => _.first(j?.rows[i]),
                  );

                  // Execute mutation.
                  return MutationSenderIdUpload({
                    variables: {
                      senderIdDirectoryId: passOn?.senderIdDirectoryId,
                      senderIds: _senderIds,
                    },
                  }).then((jp) => {
                    // On Successful response from the mutation.
                    if (
                      "UPSERT_SUCCESSFUL" === jp?.data?.SenderIdUpload?.status
                    ) {
                      // Toast success.
                      toast(
                        "Successfully uploaded the senderIds in your senderId directory.",
                      );
                    } else {
                      // Toast failure.
                      toast(
                        jp?.data?.SenderIdUpload?.message ??
                          "Failed to upload the senderIds in your senderId directory.",
                      );
                    }
                  });
                }}
                isLoading={false}
              />
              <DownloadToExcel
                cellsData={
                  0 === _QuerySenderIdRead?.data?.SenderIdRead?.length ||
                  !_.every(
                    _.pluck(_QuerySenderIdRead?.data?.SenderIdRead, "status"),
                    (i) => _successFlags.includes(i),
                  )
                    ? []
                    : _QuerySenderIdRead?.data?.SenderIdRead.map(
                        (item, __index) => ({
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          CreatedAt: Moment(item?.createdAt).format(
                            "YYYY-MM-DD",
                          ),
                          UpdatedAt: Moment(item?.updatedAt).format(
                            "YYYY-MM-DD",
                          ),
                          SenderId: item.senderId ?? "-",
                        }),
                      )
                }
                headersData={[
                  "S.No.",
                  "CreatedAt",
                  "UpdatedAt",
                  "senderId",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
            loading={_QuerySenderIdRead?.loading}
          />
        </Flex>
        <Modal
          size="md"
          title="SenderId Create"
          isOpen={isSenderIdCreateOpen}
          onClose={onSenderIdCreateClose}
        >
          <SenderIdUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size="md"
          title="SenderId Update"
          isOpen={isSenderIdUpdateOpen}
          onClose={onSenderIdUpdateClose}
        >
          <SenderIdUpsert />
        </Modal>
        <Modal
          size="md"
          title="SenderId Delete"
          isOpen={isSenderIdDeleteOpen}
          onClose={onSenderIdDeleteClose}
        >
          <SenderIdDelete />
        </Modal>
      </Flex>
    </>
  );
}



/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
